import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Trash2, Phone, Mail } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["New", "Contacted", "Confirmed"];

export default function BookingsManager() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("All");

  const load = () => api.get("/bookings").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.put(`/bookings/${id}/status`, { status });
      toast.success(`Marked ${status}`);
      setItems(items.map((b) => b.id === id ? { ...b, status } : b));
    } catch { toast.error("Could not update"); }
  };

  const del = async (id) => {
    try { await api.delete(`/bookings/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };

  const filtered = filter === "All" ? items : items.filter((b) => b.status === filter);

  return (
    <div data-testid="bookings-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-sage-600">Inbox</div>
          <h1 className="font-serif text-4xl text-earth-900 mt-2 font-light">Bookings & Enquiries</h1>
        </div>
        <div className="flex gap-2">
          {["All", ...STATUSES].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`filter-${s.toLowerCase()}`}
              className={`px-4 py-2 rounded-full text-sm transition-colors ${
                filter === s ? "bg-earth-900 text-white" : "bg-white border border-earth-200 text-earth-700 hover:bg-earth-50"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-earth-100 divide-y divide-earth-100">
        {filtered.length === 0 && <div className="p-10 text-center text-earth-500">No enquiries.</div>}
        {filtered.map((b) => (
          <div key={b.id} className="p-5 flex flex-wrap items-start gap-4 justify-between" data-testid={`booking-row-${b.id}`}>
            <div className="min-w-[200px]">
              <div className="font-serif text-xl text-earth-900">{b.name}</div>
              <div className="text-sm text-earth-600 mt-1 flex flex-wrap gap-3">
                <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" strokeWidth={1.5} />{b.phone}</span>
                {b.email && <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" strokeWidth={1.5} />{b.email}</span>}
              </div>
              <div className="text-xs text-earth-500 mt-1">{new Date(b.created_at).toLocaleString()}</div>
            </div>
            <div className="flex-1 min-w-[240px] text-earth-700 text-sm">
              {b.message || <span className="text-earth-400">No message</span>}
            </div>
            <div className="flex items-center gap-2">
              <select
                className="border border-earth-200 rounded-full px-3 py-1.5 text-sm bg-white"
                value={b.status}
                onChange={(e) => setStatus(b.id, e.target.value)}
                data-testid={`booking-status-${b.id}`}
              >
                {STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
              <Button size="icon" variant="ghost" onClick={() => del(b.id)} data-testid={`booking-del-${b.id}`}>
                <Trash2 className="w-4 h-4 text-red-600" strokeWidth={1.5} />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
